import { partition } from "mt-shared/collections";
import { YesNoLimited } from "mt-shared/db";
import {
  DietWithoutAllergies,
  dietsWithoutAllergiesSet,
} from "mt-shared/diets";
import { useMemo } from "react";

type MinimalDiet = { diet: string; isPossible: YesNoLimited };
type UseDietaryDietsProps<T extends MinimalDiet> = {
  dietaryRestrictions: T[];
};

export default function useDisplayDiets<T extends MinimalDiet>({
  dietaryRestrictions,
}: UseDietaryDietsProps<T>) {
  const [diets, allergens] = useMemo(
    () =>
      partition(dietaryRestrictions, ({ diet }) =>
        dietsWithoutAllergiesSet.has(diet as DietWithoutAllergies)
      ),
    [dietaryRestrictions]
  );

  const sortedDiets = useMemo(() => {
    return [...diets].sort((a, b) => {
      if (a.isPossible !== b.isPossible)
        return (
          isPossibleSorting[a.isPossible] - isPossibleSorting[b.isPossible]
        );

      return a.diet.localeCompare(b.diet);
    });
  }, [diets]);

  const includedAllergens = useMemo(
    () =>
      allergens
        .filter(
          ({ isPossible }) => isPossible === "no" || isPossible === "limited"
        )
        .sort((a, b) => a.diet.localeCompare(b.diet)),
    [allergens]
  );

  return { diets, allergens, sortedDiets, includedAllergens };
}

const isPossibleSorting = {
  yes: 0,
  limited: 1,
  no: 2,
} as const;
